import { inflateRaw } from './inflate.js';

/**
 * Reading an `.xlsx` file: a ZIP archive of XML, opened far enough to get a grid of strings
 * out of the first sheet and no further.
 *
 * Excel is not a nice-to-have here. A shop that has kept a customer list has kept it in Excel
 * or in Google Sheets, and telling that owner to "save as CSV" first is asking them to learn a
 * file format before they can try the product — which is a step most of them will simply not
 * take. So the file they actually have is the file this reads.
 *
 * What it deliberately does NOT do: formulas (the cached value is read instead, which is what
 * the person saw on screen), styles, number formats, merged cells, or any sheet but the first.
 * A customer list is a rectangle of text, and every one of those features is a way for this
 * file to grow into a spreadsheet engine nobody asked for.
 */

/** A worksheet as a grid of strings: no types, no formatting, just what was in the cells. */
export interface Worksheet {
  readonly rows: readonly (readonly string[])[];
}

// --- The ZIP container ----------------------------------------------------------------------

const END_OF_CENTRAL_DIRECTORY = 0x06_05_4b_50;
const CENTRAL_FILE_HEADER = 0x02_01_4b_50;

interface ZipEntry {
  readonly name: string;
  readonly compressionMethod: number;
  readonly compressedSize: number;
  readonly localHeaderOffset: number;
}

function readUint16(bytes: Uint8Array, offset: number): number {
  return (bytes[offset] as number) | ((bytes[offset + 1] as number) << 8);
}

function readUint32(bytes: Uint8Array, offset: number): number {
  return (
    ((bytes[offset] as number) |
      ((bytes[offset + 1] as number) << 8) |
      ((bytes[offset + 2] as number) << 16) |
      ((bytes[offset + 3] as number) << 24)) >>>
    0
  );
}

/**
 * Find the end-of-central-directory record by scanning backwards.
 *
 * Backwards because the record is the LAST thing in the file but has a variable-length comment
 * after its fixed part — there is no other way to locate it, which is why every ZIP reader
 * ever written begins this way.
 */
function findCentralDirectory(bytes: Uint8Array): { offset: number; count: number } {
  const earliest = Math.max(0, bytes.length - 0xff_ff - 22);
  for (let index = bytes.length - 22; index >= earliest; index -= 1) {
    if (readUint32(bytes, index) !== END_OF_CENTRAL_DIRECTORY) continue;
    return { count: readUint16(bytes, index + 10), offset: readUint32(bytes, index + 16) };
  }
  throw new Error('That file is not a readable Excel workbook');
}

function readEntries(bytes: Uint8Array): Map<string, ZipEntry> {
  const { offset, count } = findCentralDirectory(bytes);
  const entries = new Map<string, ZipEntry>();
  let cursor = offset;

  for (let index = 0; index < count; index += 1) {
    if (readUint32(bytes, cursor) !== CENTRAL_FILE_HEADER) break;
    const nameLength = readUint16(bytes, cursor + 28);
    const extraLength = readUint16(bytes, cursor + 30);
    const commentLength = readUint16(bytes, cursor + 32);
    const name = new TextDecoder().decode(bytes.subarray(cursor + 46, cursor + 46 + nameLength));
    entries.set(name, {
      name,
      compressionMethod: readUint16(bytes, cursor + 10),
      compressedSize: readUint32(bytes, cursor + 20),
      localHeaderOffset: readUint32(bytes, cursor + 42),
    });
    cursor += 46 + nameLength + extraLength + commentLength;
  }

  return entries;
}

/**
 * The bytes of one entry, decompressed.
 *
 * The name and extra-field lengths are re-read from the LOCAL header rather than trusted from
 * the central directory: the two are allowed to differ, and the local one is what says where
 * this entry's data actually starts.
 */
function readEntry(bytes: Uint8Array, entry: ZipEntry): Uint8Array {
  const header = entry.localHeaderOffset;
  const nameLength = readUint16(bytes, header + 26);
  const extraLength = readUint16(bytes, header + 28);
  const start = header + 30 + nameLength + extraLength;
  const data = bytes.subarray(start, start + entry.compressedSize);

  if (entry.compressionMethod === 0) return data;
  if (entry.compressionMethod === 8) return inflateRaw(data);
  throw new Error('That workbook uses a compression this reader does not support');
}

function readText(bytes: Uint8Array, entries: Map<string, ZipEntry>, name: string): string | null {
  const entry = entries.get(name);
  if (!entry) return null;
  return new TextDecoder().decode(readEntry(bytes, entry));
}

// --- The XML inside -------------------------------------------------------------------------

const ENTITIES: Readonly<Record<string, string>> = Object.freeze({
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
});

/** Undo XML escaping, including the numeric forms Excel writes for unusual characters. */
export function decodeXmlText(value: string): string {
  return value.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (match, entity: string) => {
    if (entity.startsWith('#x') || entity.startsWith('#X')) {
      return String.fromCodePoint(Number.parseInt(entity.slice(2), 16));
    }
    if (entity.startsWith('#')) return String.fromCodePoint(Number.parseInt(entity.slice(1), 10));
    return ENTITIES[entity.toLowerCase()] ?? match;
  });
}

/**
 * The shared string table.
 *
 * Excel stores every distinct piece of text once and refers to it by index, so a sheet full of
 * names is mostly numbers until this is read. One string may be split across several `<t>`
 * runs when part of it is formatted differently — "Priya <b>Sharma</b>" is two runs and one
 * customer — so the runs inside each `<si>` are concatenated.
 */
function parseSharedStrings(xml: string | null): string[] {
  if (!xml) return [];
  const strings: string[] = [];
  const items = xml.match(/<si\b[^>]*>[\s\S]*?<\/si>|<si\b[^>]*\/>/g) ?? [];
  for (const item of items) {
    let text = '';
    for (const run of item.match(/<t\b[^>]*>[\s\S]*?<\/t>/g) ?? []) {
      text += decodeXmlText(run.replace(/^<t\b[^>]*>/, '').replace(/<\/t>$/, ''));
    }
    strings.push(text);
  }
  return strings;
}

/** `A` → 0, `Z` → 25, `AA` → 26. The column part of a cell reference like `AB14`. */
export function columnIndexOf(reference: string): number {
  const letters = /^([A-Z]+)/.exec(reference.toUpperCase())?.[1] ?? '';
  let index = 0;
  for (const letter of letters) index = index * 26 + (letter.charCodeAt(0) - 64);
  return Math.max(0, index - 1);
}

function cellText(cell: string, sharedStrings: readonly string[]): string {
  const type = /\bt="([^"]+)"/.exec(cell)?.[1] ?? 'n';

  if (type === 'inlineStr') {
    let text = '';
    for (const run of cell.match(/<t\b[^>]*>[\s\S]*?<\/t>/g) ?? []) {
      text += decodeXmlText(run.replace(/^<t\b[^>]*>/, '').replace(/<\/t>$/, ''));
    }
    return text;
  }

  const value = /<v\b[^>]*>([\s\S]*?)<\/v>/.exec(cell)?.[1];
  if (value === undefined) return '';
  const decoded = decodeXmlText(value);

  if (type === 's') {
    // A shared-string index. A file that points outside the table is corrupt, not a crash.
    const index = Number.parseInt(decoded, 10);
    return sharedStrings[index] ?? '';
  }
  return decoded;
}

/**
 * One sheet as a grid.
 *
 * Cells carry their own reference (`C7`), and Excel omits empty ones entirely — so the column
 * is taken from the reference rather than from the cell's position in the row. Without that, a
 * customer with no email would shift their phone number into the email column.
 */
function parseSheet(xml: string, sharedStrings: readonly string[]): Worksheet {
  const rows: string[][] = [];

  for (const rowXml of xml.match(/<row\b[^>]*>[\s\S]*?<\/row>|<row\b[^>]*\/>/g) ?? []) {
    const cells: string[] = [];
    for (const cellXml of rowXml.match(/<c\b[^>]*>[\s\S]*?<\/c>|<c\b[^>]*\/>/g) ?? []) {
      const reference = /\br="([A-Z]+\d+)"/i.exec(cellXml)?.[1];
      const column = reference ? columnIndexOf(reference) : cells.length;
      while (cells.length < column) cells.push('');
      cells[column] = cellText(cellXml, sharedStrings).trim();
    }
    rows.push(cells);
  }

  return { rows };
}

/**
 * The path of the first sheet in the workbook.
 *
 * "The first sheet" is the tab the owner sees on the left, which is not necessarily
 * `sheet1.xml`: renaming and reordering leave the file names alone. So the order comes from
 * the workbook, the file name from its relationships, and `sheet1.xml` is only the fallback
 * for a workbook whose relationship part is missing.
 */
function firstSheetPath(bytes: Uint8Array, entries: Map<string, ZipEntry>): string {
  const workbook = readText(bytes, entries, 'xl/workbook.xml');
  const relationships = readText(bytes, entries, 'xl/_rels/workbook.xml.rels');
  const relationshipId = workbook ? /<sheet\b[^>]*\br:id="([^"]+)"/.exec(workbook)?.[1] : undefined;

  if (relationshipId && relationships) {
    const pattern = new RegExp(`<Relationship\\b[^>]*Id="${relationshipId}"[^>]*>`);
    const target = pattern.exec(relationships)?.[0];
    const path = target ? /\bTarget="([^"]+)"/.exec(target)?.[1] : undefined;
    if (path) {
      const normalised = path.replace(/^\/?xl\//, '').replace(/^\//, '');
      if (entries.has(`xl/${normalised}`)) return `xl/${normalised}`;
    }
  }

  return 'xl/worksheets/sheet1.xml';
}

/** Read the first worksheet of an `.xlsx` workbook. */
export function readXlsxWorksheet(file: Uint8Array): Worksheet {
  const entries = readEntries(file);
  const sharedStrings = parseSharedStrings(readText(file, entries, 'xl/sharedStrings.xml'));
  const path = firstSheetPath(file, entries);
  const sheet = readText(file, entries, path);
  if (sheet === null) throw new Error('That workbook has no readable sheet');
  return parseSheet(sheet, sharedStrings);
}

/** True when these bytes look like a ZIP container, which is what every `.xlsx` is. */
export function looksLikeXlsx(file: Uint8Array): boolean {
  return file.length > 4 && file[0] === 0x50 && file[1] === 0x4b;
}
